// src/pages/school/Circulars.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup, Form,
  InputGroup, Badge, Spinner, Modal, FloatingLabel, ProgressBar
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import JoditEditor from "jodit-react";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
  writeBatch, runTransaction, increment, getDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref as sRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, hh:mm A") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";
const norm = (v = "") => String(v).toLowerCase().replace(/\s+/g, " ").trim();
const unique = (arr) => Array.from(new Set(arr));

const AUDIENCES = [
  { key: "all", label: "All" },
  { key: "students", label: "Students" },
  { key: "teachers", label: "Teachers" },
  { key: "custom", label: "Custom (Class/Section)" },
];

const PRIORITY = [
  { key: "normal", label: "Normal", variant: "secondary" },
  { key: "high", label: "High", variant: "warning" },
  { key: "urgent", label: "Urgent", variant: "danger" },
];

// stored status in doc
const STATUS = [
  { key: "active", label: "Active", variant: "success" },
  { key: "inactive", label: "Inactive", variant: "secondary" },
];

const CLASS_FALLBACK = [
  ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),
  "Foundation 6","Foundation 7","Foundation 8","Foundation 9","Foundation 10",
  "NEET","JEE","Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE",
  "Integrated","LT",
];
const SECTION_FALLBACK = ["A","B","C","D","E","F","G","H","I","J"];

/* ---------------- Templates & Tags ---------------- */
const TEMPLATES = [
  {
    key: "holiday",
    label: "Holiday Notice",
    title: "Holiday Notice",
    html: `<p>Dear all,</p><p>The school will remain closed on <strong>${dayjs().add(1, "day").format("DD MMM YYYY")}</strong> due to holiday.</p><p>Regards,<br/>School Admin</p>`
  },
  {
    key: "exam",
    label: "Exam Schedule",
    title: "Exam Schedule Update",
    html: `<p>Dear students,</p><p>Please find the updated exam schedule attached/linked. Prepare accordingly.</p><p>All the best!</p>`
  },
  {
    key: "emergency",
    label: "Urgent Announcement",
    title: "Urgent Announcement",
    html: `<p><strong>Urgent:</strong> Please check your email / school portal for immediate instructions.</p>`
  },
];

/* ---------------- form defaults ---------------- */
const DEFAULT = {
  title: "",
  message: "",         // HTML from Jodit
  audience: "all",     // all | students | teachers | custom
  className: "",
  section: "",
  attachmentURL: "",
  priority: "normal",  // normal | high | urgent
  status: "active",    // active | inactive (display may show Scheduled/Expired)
  publishAt: "",       // datetime-local string
  expiresAt: "",       // datetime-local string (optional)
  tags: [],            // array of strings
};

/* ---------------- form modal ---------------- */
function CircularFormModal({
  show,
  onHide,
  initial,
  onSave,
  classes = [],
  schoolId,
}) {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  // Storage upload
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || "",
        message: initial.message || "",
        audience: initial.audience || "all",
        className: initial.className || "",
        section: initial.section || "",
        attachmentURL: initial.attachmentURL || "",
        priority: initial.priority || "normal",
        status: initial.status || "active",
        publishAt: initial.publishAt ? dayjs(toDate(initial.publishAt)).format("YYYY-MM-DDTHH:mm") : "",
        expiresAt: initial.expiresAt ? dayjs(toDate(initial.expiresAt)).format("YYYY-MM-DDTHH:mm") : "",
        tags: Array.isArray(initial.tags) ? initial.tags : [],
      });
    } else {
      setForm(DEFAULT);
    }
    setUploading(false);
    setUploadPct(0);
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const classOptions = useMemo(() => {
    const fromFs = (classes || []).map(c => (c.className || c.name || "").trim()).filter(Boolean);
    const uniq = unique(fromFs).sort();
    return uniq.length ? uniq : CLASS_FALLBACK;
  }, [classes]);

  const sectionOptions = useMemo(() => {
    if (!form.className) return [];
    const secs = (classes || [])
      .filter(c => (c.className || c.name) === form.className)
      .map(c => c.section)
      .filter(Boolean);
    const uniq = unique(secs).sort();
    return uniq.length ? uniq : SECTION_FALLBACK;
  }, [classes, form.className]);

  const addTag = (raw) => {
    const v = raw.replace(/[,\s]+/g, " ").trim();
    if (!v) return;
    setForm(f => ({ ...f, tags: unique([...(f.tags || []), v]) }));
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: (f.tags || []).filter(x => x !== t) }));

  const onTemplatePick = (key) => {
    const tpl = TEMPLATES.find(t => t.key === key);
    if (!tpl) return;
    setForm(f => ({
      ...f,
      title: f.title || tpl.title,
      message: f.message ? f.message : tpl.html
    }));
  };

  const onUploadFile = () => {
    if (!fileRef.current?.files?.[0] || !schoolId) return;
    const file = fileRef.current.files[0];
    const storage = getStorage();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    const path = `schools/${schoolId}/circulars/${Date.now()}_${safeName}`;
    const task = uploadBytesResumable(sRef(storage, path), file, { contentType: file.type });

    setUploading(true);
    setUploadPct(0);

    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploadPct(pct);
      },
      (err) => {
        setUploading(false);
        setUploadPct(0);
        Swal.fire({ icon: "error", title: "Upload failed", text: err?.message || "Storage error" });
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setUploading(false);
        setUploadPct(100);
        setForm(f => ({ ...f, attachmentURL: url }));
        Swal.fire({ icon: "success", title: "File uploaded", timer: 900, showConfirmButton: false });
      }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return Swal.fire({ icon: "warning", title: "Title is required" });
    if (!form.message || !form.message.trim()) return Swal.fire({ icon: "warning", title: "Message is required" });
    if (form.audience === "custom" && !form.className.trim()) {
      return Swal.fire({ icon: "warning", title: "Select class for custom audience" });
    }
    if (form.attachmentURL && !/^https?:\/\//i.test(form.attachmentURL.trim())) {
      return Swal.fire({ icon: "info", title: "Add http:// or https:// to attachment URL" });
    }

    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        message: form.message, // HTML from Jodit
        audience: form.audience,
        className: form.className.trim(),
        section: form.section.trim(),
        attachmentURL: form.attachmentURL.trim(),
        priority: form.priority,
        status: form.status,
        publishAt: form.publishAt ? new Date(form.publishAt) : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
        tags: form.tags || [],
      });
      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? "Edit Circular" : "Create Circular"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={8}>
              <FloatingLabel label="Title *">
                <Form.Control
                  value={form.title}
                  onChange={(e) => change("title", e.target.value)}
                  placeholder="Holiday Notice for Independence Day"
                  required
                />
              </FloatingLabel>
            </Col>
            <Col md={4}>
              <FloatingLabel label="Priority">
                <Form.Select
                  value={form.priority}
                  onChange={(e) => change("priority", e.target.value)}
                >
                  {PRIORITY.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold">Message *</div>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    onChange={(e) => { if (e.target.value) onTemplatePick(e.target.value); e.target.value = ""; }}
                    defaultValue=""
                    style={{ width: 220 }}
                  >
                    <option value="">Insert template…</option>
                    {TEMPLATES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </Form.Select>
                </div>
              </div>
              <div className="border rounded" style={{ overflow: "hidden" }}>
                <JoditEditor
                  ref={editorRef}
                  value={form.message}
                  config={{
                    readonly: false,
                    minHeight: 220,
                    toolbarSticky: false,
                    askBeforePasteFromWord: false,
                    askBeforePasteHTML: false,
                    uploader: { insertImageAsBase64URI: true },
                  }}
                  onBlur={(newContent) => change("message", newContent)}
                />
              </div>
              <div className="form-text">Rich text is sanitized before display.</div>
            </Col>

            <Col md={4}>
              <FloatingLabel label="Audience">
                <Form.Select
                  value={form.audience}
                  onChange={(e) => change("audience", e.target.value)}
                >
                  {AUDIENCES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={4}>
              <FloatingLabel label="Class">
                <Form.Select
                  value={form.className}
                  onChange={(e) => change("className", e.target.value)}
                  disabled={form.audience !== "custom"}
                >
                  <option value="">{form.audience === "custom" ? "— Select class —" : "— (not needed) —"}</option>
                  {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={4}>
              <FloatingLabel label="Section">
                <Form.Select
                  value={form.section}
                  onChange={(e) => change("section", e.target.value)}
                  disabled={form.audience !== "custom" || !form.className}
                >
                  <option value="">{form.audience === "custom" ? "— Optional —" : "— (not needed) —"}</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={8}>
              <FloatingLabel label="Attachment URL">
                <Form.Control
                  type="url"
                  placeholder="https://example.com/notice.pdf"
                  value={form.attachmentURL}
                  onChange={(e) => change("attachmentURL", e.target.value)}
                />
              </FloatingLabel>
              <div className="form-text">Or upload a file below; the link will fill automatically.</div>

              <div className="d-flex gap-2 mt-2">
                <Form.Control type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.ppt,.pptx" />
                <Button variant="outline-primary" onClick={onUploadFile} disabled={!schoolId || uploading}>
                  {uploading ? "Uploading…" : "Upload"}
                </Button>
              </div>
              {uploading && <ProgressBar className="mt-2" now={uploadPct} label={`${uploadPct}%`} />}
            </Col>

            <Col md={4}>
              <FloatingLabel label="Status">
                <Form.Select
                  value={form.status}
                  onChange={(e) => change("status", e.target.value)}
                >
                  {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Publish at">
                <Form.Control
                  type="datetime-local"
                  value={form.publishAt}
                  onChange={(e) => change("publishAt", e.target.value)}
                />
              </FloatingLabel>
              <div className="form-text">If empty, treated as published immediately.</div>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Expires at">
                <Form.Control
                  type="datetime-local"
                  value={form.expiresAt}
                  onChange={(e) => change("expiresAt", e.target.value)}
                />
              </FloatingLabel>
              <div className="form-text">After this, circular is auto-marked inactive.</div>
            </Col>

            <Col md={12}>
              <Form.Label className="mb-1">Tags</Form.Label>
              <InputGroup>
                <Form.Control
                  placeholder="Type a tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag(e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                />
              </InputGroup>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {(form.tags || []).map(t => (
                  <Badge key={t} bg="light" text="dark" className="border">
                    {t}{" "}
                    <Button size="sm" variant="link" className="p-0 ms-1" onClick={() => removeTag(t)}>
                      ✕
                    </Button>
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function CircularsPage() {
  const { school, loading: loadingSchool } = useSchool();

  // auth role + me uid (needed to mark read)
  const [role, setRole] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setMe(u || null);
      if (!u) { setRole(null); return; }
      const unsub = onSnapshot(doc(db, "users", u.uid), (snap) => {
        setRole((snap.data() || {}).role || null);
      });
      return () => unsub();
    });
    return () => off();
  }, []);

  const canEdit = useMemo(() => {
    const r = String(role || "").toLowerCase();
    return ["superadmin","schooladmin","admin","school","school_admin"].includes(r);
  }, [role]);

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // referenced classes (for form)
  const [classesData, setClassesData] = useState([]);

  // targetable users (for writer)
  const [studentsData, setStudentsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);

  // my read state map: circularId -> "read" | "unread" | null
  const [myState, setMyState] = useState(new Map());

  // filters
  const [qText, setQText] = useState("");
  const [cls, setCls] = useState("all");
  const [sec, setSec] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [audFilter, setAudFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [myFilter, setMyFilter] = useState("all"); // all | unread | read
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const searchRef = useRef(null);

  // subscribe circulars
  useEffect(() => {
    if (!school?.id) return;
    setLoading(true);
    const col = collection(db, "schools", school.id, "circulars");
    const unsub = onSnapshot(
      query(col, orderBy("publishAt", "desc")),
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            title: x.title || "—",
            message: x.message || "",
            audience: x.audience || "all",
            className: x.className || "",
            section: x.section || "",
            attachmentURL: x.attachmentURL || "",
            priority: x.priority || "normal",
            status: x.status || "active",
            publishAt: toDate(x.publishAt),
            expiresAt: toDate(x.expiresAt),
            createdAt: toDate(x.createdAt),
            targetCount: x.targetCount || 0,
            readCount: x.readCount || 0,
            tags: Array.isArray(x.tags) ? x.tags : [],
          };
        });
        setRows(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [school?.id]);

  // subscribe classes for options
  useEffect(() => {
    if (!school?.id) return;
    const colClasses = collection(db, "schools", school.id, "classes");
    const unsub = onSnapshot(query(colClasses, orderBy("className", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          className: data.className || data.name || "",
          section: data.section || "",
        };
      });
      setClassesData(list);
    });
    return () => unsub();
  }, [school?.id]);

  // target sources
  useEffect(() => {
    if (!school?.id) return;

    const colStudents = collection(db, "schools", school.id, "students");
    const offStu = onSnapshot(colStudents, (snap) => {
      const list = snap.docs.map(d => {
        const s = d.data() || {};
        return {
          id: d.id,
          name: s.name || s.displayName || "",
          className: s.className || "",
          section: s.section || "",
          status: (s.status || "active").toLowerCase(),
          uid: s.uid || s.userId || (s.auth && s.auth.uid) || "",
        };
      }).filter(x => x.uid);
      setStudentsData(list);
    });

    const colTeachers = collection(db, "schools", school.id, "teachers");
    const offTch = onSnapshot(colTeachers, (snap) => {
      const list = snap.docs.map(d => {
        const t = d.data() || {};
        return {
          id: d.id,
          name: t.name || "",
          email: t.email || "",
          uid: t.uid || t.userId || (t.auth && t.auth.uid) || "",
        };
      }).filter(x => x.uid);
      setTeachersData(list);
    });

    return () => { offStu(); offTch(); };
  }, [school?.id]);

  // my per-circular target (N listeners; OK for tens/hundreds)
  useEffect(() => {
    if (!school?.id || !me?.uid) return;
    const offs = [];

    rows.forEach((r) => {
      const tRef = doc(db, "schools", school.id, "circulars", r.id, "targets", me.uid);
      const off = onSnapshot(tRef, (snap) => {
        const st = snap.exists() ? (snap.data().status || "unread") : null;
        setMyState(prev => {
          const n = new Map(prev);
          n.set(r.id, st);
          return n;
        });
      });
      offs.push(off);
    });

    return () => offs.forEach(f => f());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school?.id, me?.uid, rows.length]);

  // filter dropdown options from rows
  const classesList = useMemo(() => ["all", ...unique(rows.map(r => r.className).filter(Boolean)).sort()], [rows]);
  const sectionsList = useMemo(() => ["all", ...unique(rows.map(r => r.section).filter(Boolean)).sort()], [rows]);
  const tagList = useMemo(() => ["all", ...unique(rows.flatMap(r => r.tags || [])).sort()], [rows]);

  // derived status display (Scheduled/Expired)
  const deriveStatusBadge = (row) => {
    const now = dayjs();
    if (row.publishAt && dayjs(row.publishAt).isAfter(now)) {
      return { label: "Scheduled", variant: "info" };
    }
    if (row.expiresAt && dayjs(row.expiresAt).isBefore(now)) {
      return { label: "Expired", variant: "dark" };
    }
    const st = STATUS.find(s => s.key === row.status) || STATUS[0];
    return { label: st.label, variant: st.variant };
  };

  const filtered = useMemo(() => {
    const monthStart = dayjs(month + "-01").startOf("month");
    const monthEnd = dayjs(month + "-01").startOf("month").add(1, "month");

    const q = qText.trim().toLowerCase();
    return rows.filter((r) => {
      // month filter
      const pub = dayjs(r.publishAt || r.createdAt || null);
      if (!pub.isValid() || !(pub.isSame(monthStart) || (pub.isAfter(monthStart) && pub.isBefore(monthEnd)))) {
        return false;
      }

      if (cls !== "all" && r.className !== cls) return false;
      if (sec !== "all" && r.section !== sec) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (audFilter !== "all" && r.audience !== audFilter) return false;
      if (tagFilter !== "all" && !(r.tags || []).includes(tagFilter)) return false;

      if (myFilter !== "all" && me?.uid) {
        const st = myState.get(r.id);
        if (myFilter === "unread" && st === "read") return false;
        if (myFilter === "read" && st !== "read") return false;
      }

      if (q) {
        const plain = (r.message || "").replace(/<[^>]+>/g, " ");
        const hay = [r.title, plain, r.className, r.section, ...(r.tags || [])].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, month, qText, cls, sec, statusFilter, audFilter, tagFilter, myFilter, myState, me?.uid]);

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setShowModal(true); };

  // ---------- Targeting helpers ----------
  const computeRecipients = (payload) => {
    const a = payload.audience || "all";
    const className = (payload.className || "").trim();
    const section = (payload.section || "").trim();
    const activeStudents = studentsData.filter(s => (s.status || "active") !== "inactive");

    if (a === "all") {
      return [
        ...activeStudents.map(s => ({ uid: s.uid, role: "student" })),
        ...teachersData.map(t => ({ uid: t.uid, role: "teacher" })),
      ];
    }
    if (a === "teachers") {
      return teachersData.map(t => ({ uid: t.uid, role: "teacher" }));
    }
    if (a === "students") {
      return activeStudents.map(s => ({ uid: s.uid, role: "student" }));
    }
    // custom → class/section filtered students only
    return activeStudents
      .filter(s => s.className === className && (!section || s.section === section))
      .map(s => ({ uid: s.uid, role: "student" }));
  };

  const writeTargetsChunked = async (circularRef, recipients) => {
    // Include schoolId for collectionGroup queries / rules
    const CHUNK = 450;
    for (let i = 0; i < recipients.length; i += CHUNK) {
      const batch = writeBatch(db);
      const slice = recipients.slice(i, i + CHUNK);
      slice.forEach(r => {
        const tRef = doc(db, "schools", school.id, "circulars", circularRef.id, "targets", r.uid);
        batch.set(tRef, {
          uid: r.uid,
          role: r.role,
          status: "unread",
          schoolId: school.id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      });
      await batch.commit();
    }
  };

  // ---------- Optimistic create/update/delete ----------
  const [optimisticStack, setOptimisticStack] = useState([]);

  const save = async (payload) => {
    const base = {
      title: payload.title,
      message: payload.message,
      audience: payload.audience,
      className: payload.className,
      section: payload.section,
      attachmentURL: payload.attachmentURL,
      priority: payload.priority,
      status: payload.status,
      publishAt: payload.publishAt || null,
      expiresAt: payload.expiresAt || null,
      tags: payload.tags || [],
      updatedAt: serverTimestamp(),
    };

    const recipients = computeRecipients(payload);
    const targetCount = recipients.length;

    if (editing) {
      // optimistic update
      const prev = rows;
      const optimistic = rows.map(r => r.id === editing.id ? { ...r, ...base, targetCount } : r);
      setOptimisticStack(s => [...s, prev]);
      setRows(optimistic);

      try {
        const ref = doc(db, "schools", school.id, "circulars", editing.id);
        await updateDoc(ref, { ...base, targetCount });
        await writeTargetsChunked(ref, recipients);
        Swal.fire({ icon: "success", title: `Updated (targets: ${targetCount})`, timer: 1000, showConfirmButton: false });
      } catch (err) {
        // rollback
        const prevState = optimisticStack.pop() || prev;
        setRows(prevState);
        Swal.fire({ icon: "error", title: "Update failed", text: err?.message || "Network error" });
      }
    } else {
      // optimistic create
      const tempId = `tmp_${Date.now()}`;
      const fake = {
        id: tempId,
        ...base,
        targetCount,
        readCount: 0,
        createdAt: new Date(),
      };
      setOptimisticStack(s => [...s, rows]);
      setRows([fake, ...rows]);

      try {
        const ref = await addDoc(collection(db, "schools", school.id, "circulars"), {
          ...base,
          targetCount,
          readCount: 0,
          createdAt: serverTimestamp(),
        });
        await writeTargetsChunked(ref, recipients);
        Swal.fire({ icon: "success", title: `Created (targets: ${targetCount})`, timer: 1000, showConfirmButton: false });
      } catch (err) {
        // rollback
        const prevState = optimisticStack.pop() || rows.filter(r => r.id !== tempId);
        setRows(prevState);
        Swal.fire({ icon: "error", title: "Create failed", text: err?.message || "Network error" });
      }
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete circular?",
      text: `Remove "${row.title}" (${row.className || row.audience}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;

    // optimistic delete
    const prev = rows;
    setOptimisticStack(s => [...s, prev]);
    setRows(rows.filter(r => r.id !== row.id));

    try {
      await deleteDoc(doc(db, "schools", school.id, "circulars", row.id));
      Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
    } catch (err) {
      // rollback
      const prevState = optimisticStack.pop() || prev;
      setRows(prevState);
      Swal.fire({ icon: "error", title: "Delete failed", text: err?.message || "Network error" });
    }
  };

  // ---------- Mark as read (atomic + counter) ----------
  const markAsRead = async (row) => {
    if (!me?.uid) return;
    const tRef = doc(db, "schools", school.id, "circulars", row.id, "targets", me.uid);
    const cRef = doc(db, "schools", school.id, "circulars", row.id);

    try {
      await runTransaction(db, async (tx) => {
        const tSnap = await tx.get(tRef);
        const wasRead = tSnap.exists() && (tSnap.data()?.status === "read");
        if (!wasRead) {
          tx.set(tRef, { status: "read", updatedAt: serverTimestamp(), uid: me.uid, schoolId: school.id }, { merge: true });
          tx.update(cRef, { readCount: increment(1), updatedAt: serverTimestamp() });
        }
      });
      setMyState(prev => {
        const n = new Map(prev);
        n.set(row.id, "read");
        return n;
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to mark as read", text: err?.message || "Network error" });
    }
  };

  // ---------- Auto-publish / Auto-expire ----------
  useEffect(() => {
    if (!canEdit || !school?.id) return;
    const interval = setInterval(async () => {
      const now = dayjs();
      const candidates = rows.filter(r => {
        const scheduled = r.publishAt && dayjs(r.publishAt).isAfter(dayjs(r.createdAt || null));
        const needsActivate = r.status !== "active" && r.publishAt && dayjs(r.publishAt).isBefore(now);
        const needsExpire = r.status === "active" && r.expiresAt && dayjs(r.expiresAt).isBefore(now);
        return needsActivate || needsExpire;
      });

      for (const r of candidates) {
        const cRef = doc(db, "schools", school.id, "circulars", r.id);
        try {
          if (r.publishAt && dayjs(r.publishAt).isBefore(now) && r.status !== "active") {
            await updateDoc(cRef, { status: "active", updatedAt: serverTimestamp() });
          }
          if (r.expiresAt && dayjs(r.expiresAt).isBefore(now) && r.status === "active") {
            await updateDoc(cRef, { status: "inactive", updatedAt: serverTimestamp() });
          }
        } catch {
          // ignore transient errors
        }
      }
    }, 60 * 1000); // every 60s
    return () => clearInterval(interval);
  }, [canEdit, rows, school?.id]);

  const exportCSV = () => {
    const data = filtered.map(r => ({
      id: r.id,
      title: r.title,
      message: (r.message || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").slice(0, 1000),
      audience: r.audience,
      class: r.className,
      section: r.section,
      priority: r.priority,
      status: deriveStatusBadge(r).label,
      publishAt: fmt(r.publishAt, "YYYY-MM-DD HH:mm"),
      expiresAt: fmt(r.expiresAt, "YYYY-MM-DD HH:mm"),
      attachmentURL: r.attachmentURL || "",
      tags: (r.tags || []).join(", "),
      readCount: r.readCount || 0,
      targetCount: r.targetCount || 0,
      createdAt: fmt(r.createdAt, "YYYY-MM-DD HH:mm"),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "circulars");
    XLSX.writeFile(wb, `circulars_${school?.name || "school"}_${month}.xlsx`);
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Circulars</h5>
          <small className="text-muted">Create and share notices for {school.name}</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export CSV
            </Button>
            <Button onClick={openCreate} disabled={!canEdit}>
              <i className="bi bi-plus-lg me-1" /> New Circular
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <Card className="p-3">
        <Row className="g-2 align-items-end mb-2">
          <Col md={2}>
            <Form.Label className="mb-1">Month</Form.Label>
            <Form.Control type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Class</Form.Label>
            <Form.Select value={cls} onChange={(e) => setCls(e.target.value)}>
              {classesList.map((c) => <option key={c} value={c}>{c === "all" ? "All" : c}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Section</Form.Label>
            <Form.Select value={sec} onChange={(e) => setSec(e.target.value)}>
              {sectionsList.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Audience</Form.Label>
            <Form.Select value={audFilter} onChange={(e) => setAudFilter(e.target.value)}>
              {["all","students","teachers","custom"].map(a => (
                <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Status</Form.Label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Tag</Form.Label>
            <Form.Select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
              {tagList.map((t) => <option key={t} value={t}>{t === "all" ? "All" : t}</option>)}
            </Form.Select>
          </Col>
        </Row>

        <Row className="g-2 align-items-end mb-2">
          <Col md={6}>
            <Form.Label className="mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Title, message, tags…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1">My state</Form.Label>
            <Form.Select value={myFilter} onChange={(e) => setMyFilter(e.target.value)} disabled={!me?.uid}>
              <option value="all">All</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </Form.Select>
          </Col>
          <Col md={3} className="d-flex align-items-end justify-content-end">
            <Badge bg="light" text="dark" className="border">
              {filtered.length} / {rows.length}
            </Badge>
          </Col>
        </Row>

        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead>
              <tr>
                <th>Title</th>
                <th className="d-none d-lg-table-cell">Audience</th>
                <th className="d-none d-md-table-cell">Class</th>
                <th className="d-none d-xl-table-cell">Publish</th>
                <th className="d-none d-xl-table-cell">Expires</th>
                <th className="d-none d-lg-table-cell">Priority</th>
                <th className="d-none d-lg-table-cell">Status</th>
                <th className="d-none d-lg-table-cell">Seen</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No circulars found</td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const pr = PRIORITY.find(p => p.key === c.priority) || PRIORITY[0];
                  const st = deriveStatusBadge(c);
                  const my = myState.get(c.id);
                  const cleanHTML = DOMPurify.sanitize(c.message || "");

                  return (
                    <tr key={c.id} className={!me?.uid ? "" : (my === "read" ? "" : "table-warning")}>
                      <td style={{ minWidth: 260 }}>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 320 }}>{c.title}</div>
                        <div
                          className="text-muted small text-truncate"
                          style={{ maxWidth: 420 }}
                          dangerouslySetInnerHTML={{ __html: cleanHTML.replace(/<img[^>]*>/g, "") }}
                        />
                        <div className="mt-1 d-flex flex-wrap gap-1">
                          {(c.tags || []).map(t => <Badge key={t} bg="light" text="dark" className="border">{t}</Badge>)}
                        </div>
                        {c.attachmentURL ? (
                          <div className="small">
                            <a href={c.attachmentURL} target="_blank" rel="noreferrer">Attachment</a>
                          </div>
                        ) : null}
                      </td>
                      <td className="d-none d-lg-table-cell text-capitalize">
                        {c.audience === "custom" ? "Custom" : c.audience}
                      </td>
                      <td className="d-none d-md-table-cell">
                        {c.audience === "custom" ? [c.className, c.section].filter(Boolean).join("-") || "—" : "—"}
                      </td>
                      <td className="d-none d-xl-table-cell">{fmt(c.publishAt)}</td>
                      <td className="d-none d-xl-table-cell">{fmt(c.expiresAt)}</td>
                      <td className="d-none d-lg-table-cell">
                        <Badge bg={pr.variant}>{pr.label}</Badge>
                      </td>
                      <td className="d-none d-lg-table-cell">
                        <Badge bg={st.variant}>{st.label}</Badge>
                      </td>
                      <td className="d-none d-lg-table-cell">
                        <Badge bg="light" text="dark" className="border">
                          {c.readCount || 0} / {c.targetCount || 0}
                        </Badge>
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          title="View & mark read"
                          onClick={async () => {
                            await Swal.fire({
                              title: c.title,
                              html:
                                `<div style="text-align:left">${cleanHTML}</div>` +
                                (c.attachmentURL ? `<div class="mt-2"><a target="_blank" rel="noreferrer" href="${c.attachmentURL}">Attachment</a></div>` : ""),
                              showConfirmButton: true
                            });
                            if (me?.uid) markAsRead(c);
                          }}
                        >
                          <i className="bi bi-eye" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-2"
                          title="Edit"
                          onClick={() => { setEditing(c); setShowModal(true); }}
                          disabled={!canEdit}
                        >
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Delete"
                          onClick={() => removeRow(c)}
                          disabled={!canEdit}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <CircularFormModal
        show={showModal}
        onHide={() => { setShowModal(false); setEditing(null); }}
        initial={editing}
        onSave={async (payload) => {
          await save(payload);
          setShowModal(false);
          setEditing(null);
        }}
        classes={classesData}
        schoolId={school?.id}
      />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
        .swal2-html-container { text-align: left; }
        tr.table-warning { --bs-table-bg: rgba(255, 193, 7, .08); }
      `}</style>
    </Container>
  );
}
